import { PrescribedBurnEntity } from './../../domain/entities/prescribed-burn-entity';
import { ApiFetchError, BaseError, UnimplementedError } from '@/common/errors';
import {
  BaseHazardEntity,
  CentreEntity,
  CentreSubType,
  ClosureEntity,
  ClosureSubType,
  EnrichedWarningEntity,
  EntitySubType,
  EventEntity,
  FdrSubType,
  TotalFireBanEntity,
  GetCentresModel,
  GetClosuresModel,
  GetEventsModel,
  GetHazardsModel,
  GetIncidentsModel,
  GetS3HazardsModel,
  GetWarningByIdModel,
  GetWarningsModel,
  HAZARD_MARKERS,
  HazardEntity,
  HazardType,
  HazardsDataSource,
  HazardsRepository,
  IncidentEntity,
  IncidentSubType,
  S3HazardsDataSource,
  WarningEntity,
  WarningSubType,
  centresToSchemeMap,
  closuresToSchemeMap,
  incidentsToSchemeMap,
  localAreaGovernments,
  warningsToSchemeMap,
  HazardPriorityMapping,
  IconName,
  WarningSubTypes,
  FireDangerRatingEntity,
  FireDangerRatingEntityForecast,
} from '@/features/hazards';

import { Either, left, right } from 'fp-ts/lib/Either';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { DateTime } from 'luxon';
import { GetBaseHazardModel, GetFireDangerRatingsModelV2, GetTotalFireBansModel, fireDangerWarningsToSchemeMap } from '../models';

import { composeFireBanLga } from '@/common/helpers/compose-fire-ban-lga';
import { captureException } from '@sentry/vue';
import { lgasNameMapping } from '../../presentation/regionToLgaMappings';

export class HazardsRepositoryImpl implements HazardsRepository {
  dataSource: HazardsDataSource;
  s3DataSource: S3HazardsDataSource;

  constructor(dataSource: HazardsDataSource, s3DataSource: S3HazardsDataSource) {
    this.dataSource = dataSource;
    this.s3DataSource = s3DataSource;
  }

  /**
   * @deprecated - The getEvents method should be used instead to get the event details.
   * @param hazardType
   * @param hazardId
   * @returns
   */
  async getHazardById(hazardType: HazardType, hazardId: string): Promise<Either<BaseError, EnrichedWarningEntity>> {
    try {
      let hazardModel: GetWarningByIdModel | undefined;

      switch (hazardType) {
        case HazardType.Warning:
          hazardModel = await this.dataSource.getWarningById(hazardId);
          break;
        default:
          throw new UnimplementedError(`get ${hazardType} by id`);
      }

      return right(hazardModel);
    } catch (e) {
      captureException(e);

      if (e instanceof BaseError) {
        return left(e);
      }

      return left(new ApiFetchError(`${hazardType} - ${hazardId}`));
    }
  }

  async getHazards(hazardType: HazardType): Promise<Either<BaseError, HazardEntity[]>> {
    try {
      let hazardModels: GetHazardsModel[] = [];
      let fireDangerRatingsModels: GetFireDangerRatingsModelV2[] = [];
      let s3HazardModel: GetS3HazardsModel | null = null;

      switch (hazardType) {
        case HazardType.Incident:
          hazardModels = await this.dataSource.getIncidents();
          break;
        case HazardType.Warning:
          hazardModels = await this.dataSource.getWarnings();
          break;
        case HazardType.Centre:
          hazardModels = await this.dataSource.getCentres();
          break;
        case HazardType.Closure:
          hazardModels = await this.dataSource.getOutagesAndClosures();
          break;
        case HazardType.Event:
          hazardModels = await this.dataSource.getEvents();
          break;
        case HazardType.TotalFireBans:
          hazardModels = await this.dataSource.getTotalFireBans();
          break;
        case HazardType.FireDangerRating:
          fireDangerRatingsModels = await this.dataSource.getFireDangerRatings();
          break;
        case HazardType.PrescribedBurns:
          s3HazardModel = await this.s3DataSource.getPrescribedBurns();
          break;
        default:
          throw new UnimplementedError(`get ${hazardType}`);
      }

      if (s3HazardModel) return right(this.sortHazardEntities(this.mapS3ModelsToEntities(hazardType, s3HazardModel)));

      if (hazardType === HazardType.FireDangerRating) {
        return right(this.mapFdrV2ModelsToEntities(fireDangerRatingsModels));
      }

      return right(this.mapModelsToEntities({ hazardModels, hazardType }));
    } catch (e) {
      captureException(e);
      return left(new ApiFetchError(hazardType));
    }
  }

  // API Models
  mapModelsToEntities({ hazardModels, hazardType }: { hazardModels: GetHazardsModel[]; hazardType: HazardType }): HazardEntity[] {
    switch (hazardType) {
      case HazardType.Warning:
      case HazardType.Incident:
      case HazardType.Centre:
      case HazardType.Closure:
        return hazardModels.map((model) => this.mapModelToEntity(model));
      case HazardType.Event:
        return this.sortHazardEntities(hazardModels.map((model) => this.mapModelToEntity(model)));
      case HazardType.TotalFireBans:
        return this.mapTotalFireBanModelToEntity(hazardModels as GetTotalFireBansModel[]);
      default:
        throw new UnimplementedError(`get ${hazardType}`);
    }
  }

  mapModelToEntity(hazardModel: GetBaseHazardModel): HazardEntity {
    const geoSource = hazardModel?.['geo-source'] as FeatureCollection;
    const geoSourceFeatures = geoSource?.['features'] ?? [];

    const points = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Point');
    const pointFeatures = this.enrichFeatures(points, hazardModel);

    const polylines = geoSourceFeatures.filter(
      (feature) => feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
    );
    const polylineFeatures = this.enrichFeatures(polylines, hazardModel);

    const polygons = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon');
    const polygonFeatures = this.enrichFeatures(polygons, hazardModel);

    const baseHazardEntity: BaseHazardEntity = {
      id: hazardModel.id!,
      entityType: hazardModel.entityType as HazardType,
      entitySubType: hazardModel.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType],

      eventId: hazardModel.event!,

      title: hazardModel.name,
      publishedAt: hazardModel['published-date-time'],
      issuedAt: hazardModel['issued-date-time'],

      polygonFeatures,
      polylineFeatures,
      pointFeatures,
      pointFeature: pointFeatures.at(0),
    };

    let partialHazardEntity: Partial<HazardEntity> = {};

    switch (hazardModel.entityType) {
      case HazardType.Warning:
        partialHazardEntity = this.mapWarningModelToPartialEntity(hazardModel as GetWarningsModel);
        break;
      case HazardType.Incident:
        partialHazardEntity = this.mapIncidentModelToPartialEntity(hazardModel as GetIncidentsModel);
        break;
      case HazardType.Centre:
        partialHazardEntity = this.mapCentreModelToPartialEntity(hazardModel as GetCentresModel);
        break;
      case HazardType.Closure:
        partialHazardEntity = this.mapClosureModelToPartialEntity(hazardModel as GetClosuresModel);
        break;
      case HazardType.Event:
        partialHazardEntity = this.mapEventModelToPartialEntity(hazardModel as GetEventsModel);
        break;

      default:
        break;
    }

    const hazardEntity: HazardEntity = { ...baseHazardEntity, ...(partialHazardEntity as HazardEntity) };

    return hazardEntity;
  }

  mapEventModelToPartialEntity(eventModel: GetEventsModel): Partial<EventEntity> {
    const banDate = eventModel['date-of-ban'];
    return {
      publishedAt: eventModel['published-date-time'],
      issuingAgency: eventModel['issuing-agency'],
      eventSummary: eventModel['web-message'],
      eventSummaryText: eventModel['web-text-message'],
      dateOfBan: banDate ? DateTime.fromISO(banDate) : undefined,
      banInformation: eventModel['ban-information'],
      rawEvent: eventModel,
    };
  }

  /**
   * Sort hazard entities by priority and, if needed, the published date.
   */
  sortHazardEntities(hazardEntities: HazardEntity[]): HazardEntity[] {
    return hazardEntities.sort((a, b) => {
      const aPriority = HazardPriorityMapping[a.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;
      const bPriority = HazardPriorityMapping[b.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;

      /**
       * This is to ensure warnings of the same type (Bushfire) are sorted by priority.
       * @example
       *  - x Emergency Warning comes before x Watch and Act Warning
       */
      if (Math.floor(aPriority) !== Math.floor(bPriority)) {
        return bPriority - aPriority;
      }

      // Sort by published date if the priority is the same
      const aPublishedAt = a.publishedAt as string;
      const bPublishedAt = b.publishedAt as string;

      if (aPublishedAt && bPublishedAt) {
        const aMillis = DateTime.fromISO(aPublishedAt).toMillis();
        const bMillis = DateTime.fromISO(bPublishedAt).toMillis();

        return bMillis - aMillis;
      }

      /**
       * This is to ensure warnings of different types (Bushfire, Flood, etc) but the same alert level (Emergency, Watch and Act, etc)
       * are sorted by priority.
       * @example
       *  - Bushfire Emergency Warning comes before Flood Emergency Warning
       */
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }

      // Sort by title (alphabetically) if the published date is the same
      const aTitle = a.title;
      const bTitle = b.title;

      if (aTitle && bTitle) {
        return aTitle.localeCompare(bTitle);
      }

      return 0;
    });
  }

  mapWarningModelToPartialEntity(warningModel: GetWarningsModel): Partial<WarningEntity> {
    const entitySubType = warningModel['entitySubType'];
    const scheme = warningsToSchemeMap[entitySubType as WarningSubType];

    if (entitySubType === WarningSubTypes.OtherWarningWarningEntity && warningModel['warning-icon']) {
      scheme.icon = HAZARD_MARKERS[warningModel['warning-icon'] as IconName] ?? scheme.icon;
    }

    return {
      ...scheme,
      description: this.composeWarningDescription(warningModel),
      warningType: warningModel['warning-type'],
      publishingStatus: warningModel['publishing-status'],
      geoSource: warningModel['geo-source'],
      alertLine: warningModel['alert-line'],
      safeToLeave: warningModel['safe-to-leave'],
      impactToHomesNote: warningModel['impact-to-homes-note'],
      telephoneWarning: warningModel['telephone-warning'],
      alertLevelNote: warningModel['alert-level-note'],
      threatToHomesNote: warningModel['threat-to-homes-note'],
      fireLocationNote: warningModel['fire-location-note'],
      fireDirectionNote: warningModel['fire-direction-note'],
      whatToDoNote: warningModel['what-to-do-note'],
      safestRouteNote: warningModel['safest-route-note'],
      sortingPriority: warningModel['sorting-priority'],
      actionStatement: warningModel['action-statement'],
    };
  }

  mapIncidentModelToPartialEntity(incidentModel: GetIncidentsModel): Partial<IncidentEntity> {
    const entitySubType = incidentModel['entitySubType'];
    const scheme = incidentsToSchemeMap[entitySubType as IncidentSubType];

    const incidentIcon = incidentModel['incident-icon'] as IconName | undefined;

    if (entitySubType === IncidentSubType.OtherIncidentEntity && incidentIcon) {
      scheme.icon = HAZARD_MARKERS[incidentIcon] ?? scheme?.icon;
    }

    return {
      ...scheme,
      lga: incidentModel['lga'],
      incidentType: incidentModel['incident-type'],
      reportedAt: incidentModel['start-date-time'],
      /**
       * The `updated-date-time` is used for both `publishedAt` and `updatedAt`.
       *
       * Some incidents don't have an `updated-date-time` and only have a `published-date-time`.
       * As such, we fallback to using the `published-date-time` if the `updated-date-time` is not available.
       */
      publishedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      updatedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      incidentStatus: incidentModel['incident-status'],
      description: this.composeIncidentDescription(incidentModel),
      reportedNear: this.composeIncidentReportedNear(incidentModel),
      reportedIn:
        incidentModel.entitySubType !== EntitySubType.EarthquakeIncidentEntity ? incidentModel['suburbs']?.at(0)?.toUpperCase() : undefined,
      earthquakeMagnitude: incidentModel['earthquake-magnitude'],
      earthquakeDepth: incidentModel['earthquake-depth'],
      submitFeltReportNote: incidentModel['submit-felt-report-note'],
    };
  }

  mapClosureModelToPartialEntity(closureModel: GetClosuresModel): Partial<ClosureEntity> {
    const entitySubType = closureModel['entitySubType'];
    const scheme = closuresToSchemeMap[entitySubType as ClosureSubType];

    return {
      ...scheme,
      description: closureModel['headline'] ?? '',
    };
  }

  mapCentreModelToPartialEntity(centreModel: GetClosuresModel): Partial<CentreEntity> {
    const entitySubType = centreModel['entitySubType'];
    const scheme = centresToSchemeMap[entitySubType as CentreSubType];

    return {
      ...scheme,
      description: centreModel['headline'] ?? '',
    };
  }

  mapTotalFireBanModelToEntity(totalFireBanModel: GetTotalFireBansModel[]): HazardEntity[] {
    const activeTotalFireBans = totalFireBanModel.map((model) => {
      const banDate = model['date-of-ban'];
      const expiry = model['expiry'];

      const banLevel = model['ban-level'];
      const isActive = banLevel === 'Ban';
      const rawLgaName = model['fire-ban-lga'];
      const sanitisedLga = composeFireBanLga(rawLgaName);

      const scheme =
        warningsToSchemeMap[isActive ? WarningSubTypes.TotalFireBanWarningEntity : WarningSubTypes.NoTotalFireBanWarningEntity];

      let description;

      switch (banLevel) {
        case 'Ban':
          description = 'Total Fire Ban';
          break;
        case 'No ban':
          description = 'No Total Fire Ban';
          break;
        case 'No ban - revoked':
          description = 'Total Fire Ban - Revoked';
          break;
        default:
          description = 'Total Fire Ban';
          break;
      }

      return {
        ...scheme,
        // We are using the rawLgaName as the id this enables us to
        // efficiently filter the map layers (given we're using tile ets)
        id: rawLgaName,
        banLevel,
        description,
        isActive,
        eventId: model.event!,
        title: sanitisedLga,
        rawLgaName,
        banDuration: model['ban-duration'],
        lga: sanitisedLga,
        entitySubType: model['entitySubType'] as WarningSubType,
        publishedAt: model['published-date-time'],
        entityType: HazardType.TotalFireBans,
        dateOfBan: banDate ? DateTime.fromISO(banDate) : undefined,
        expiry: expiry ? DateTime.fromISO(expiry) : undefined,
      } as TotalFireBanEntity;
    });

    // This will be used to enrich the TFB items with the inactive fire bans
    const inActiveTotalFireBans = localAreaGovernments.map((lga) => {
      const sanitisedLga = composeFireBanLga(lga);
      return {
        ...warningsToSchemeMap[WarningSubTypes.NoTotalFireBanWarningEntity],
        id: lga,
        title: sanitisedLga,
        eventId: '',
        description: 'No Total Fire Ban',
        banLevel: 'No ban',
        banDuration: 'All day',
        isActive: false,
        rawLgaName: lga,
        lga: sanitisedLga,
        polygonFeatures: [],
        entitySubType: WarningSubTypes.TotalFireBanWarningEntity,
        publishedAt: totalFireBanModel?.at(0)?.['published-date-time'],
        entityType: HazardType.TotalFireBans,
      } as TotalFireBanEntity;
    });

    // Group the fire ban by ban level, then sort each group alphabetically
    const activeFireBanItems = activeTotalFireBans.filter((item) => item.isActive).sort((a, b) => a.lga.localeCompare(b.lga));
    const inactiveRemoteFireBanItems = activeTotalFireBans.filter((item) => !item.isActive);
    const inactiveFireBanItems = inactiveRemoteFireBanItems
      .concat(inActiveTotalFireBans)
      .filter((item) => !item.isActive)
      .sort((a, b) => a.lga.localeCompare(b.lga));

    return activeFireBanItems.concat(inactiveFireBanItems);
  }

  // S3 Models
  mapS3ModelsToEntities(hazardType: HazardType, hazardModel: GetS3HazardsModel): HazardEntity[] {
    switch (hazardType) {
      case HazardType.PrescribedBurns:
        return hazardModel.features.map((model) => this.mapPbModelToEntity(model));
      default:
        throw new UnimplementedError(`get ${hazardType}`);
    }
  }

  // Fire Danger Ratings Model (V2)
  mapFdrV2ModelsToEntities(models: GetFireDangerRatingsModelV2[]): HazardEntity[] {
    return models.map((model) => this.mapFdrV2ModelToEntity(model));
  }

  mapFdrV2ModelToEntity(model: GetFireDangerRatingsModelV2): HazardEntity {
    const forecasts: FireDangerRatingEntityForecast[] = [];

    for (const [, forecast] of Object.entries(model.forecasts ?? {})) {
      let fdrSubType: FdrSubType;
      switch (forecast.fireDanger) {
        case 'Catastrophic':
          fdrSubType = FdrSubType.CatastrophicFdrEntity;
          break;
        case 'Extreme':
          fdrSubType = FdrSubType.ExtremeFdrEntity;
          break;
        case 'High':
          fdrSubType = FdrSubType.HighFdrEntity;
          break;
        case 'Moderate':
          fdrSubType = FdrSubType.ModerateFdrEntity;
          break;
        case 'No Rating':
        default:
          fdrSubType = FdrSubType.NoRatingFdrEntity;
          break;
      }

      forecasts.push({
        fdrSubType: fdrSubType,
        fireDanger: forecast.fireDanger ?? '',
        behaviourIndex: forecast.behaviourIndex ?? '',
        scheme: fireDangerWarningsToSchemeMap[fdrSubType],
      });
    }

    const baseHazardEntity: FireDangerRatingEntity = {
      id: model.area ?? '',
      entityType: HazardType.FireDangerRating,
      title: model.description ?? '',
      issuedAt: this.safeParseIsoDate(model.issueDateTime),
      forecasts,
    };

    return baseHazardEntity;
  }

  /**
   * Safely parses an ISO date string and returns a valid ISO string or undefined.
   * @param dateString The ISO date string to parse.
   * @returns A valid ISO date string or undefined if the input is invalid.
   * @example
   * safeParseIsoDate('2023-10-01T12:00:00Z') => '2023-10-01T12:00:00.000Z'
   * safeParseIsoDate('invalid-date') => undefined
   * safeParseIsoDate(undefined) => undefined
   */
  private safeParseIsoDate(dateString: string | undefined): string | undefined {
    if (!dateString) return undefined;

    const parsedDate = DateTime.fromISO(dateString);
    return parsedDate.isValid ? parsedDate.toISO() : undefined;
  }

  mapPbModelToEntity(feature: Feature<Geometry, GeoJsonProperties>): HazardEntity {
    const burnCenterCoordinates = [
      (feature.properties?.['burn_target_long'] as number | undefined) ?? 0,
      (feature.properties?.['burn_target_lat'] as number | undefined) ?? 0,
    ];

    const pointFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: burnCenterCoordinates,
      },
      properties: {
        id: feature.properties?.['burn_id'],
        featureId: feature.id,
        entityType: HazardType.PrescribedBurns,
        'icon-name': 'ew-prescribed-burn-or-burn-off',
      },
    } as Feature<Geometry, GeoJsonProperties>;

    const baseHazardEntity: PrescribedBurnEntity = {
      id: feature.properties?.['burn_id'],
      entityType: HazardType.PrescribedBurns,
      title: 'Prescribed Burn',
      description: feature.properties?.['location'],
      icon: HAZARD_MARKERS['ew-prescribed-burn-or-burn-off'],

      burnTargetDate: feature.properties?.['burn_target_date'],
      publishedAt: DateTime.fromJSDate(new Date(feature.properties?.['burn_target_date_raw'])).toISO() ?? undefined,
      burnEstimatedStart: feature.properties?.['burn_est_start'],
      burnStatus: feature.properties?.['burn_stat'],
      burnLocation: feature.properties?.['location'],
      burnForestBlocks: feature.properties?.['forest_blocks'],
      burnArea: feature.properties?.['indicative_area'],
      burnPlannedAreaToday: feature.properties?.['burn_planned_area_today'],
      burnPlannedDistanceToday: feature.properties?.['burn_planned_distance_today'],
      burnCoordinates: [
        (feature.properties?.['burn_target_long'] as number | undefined) ?? 0,
        (feature.properties?.['burn_target_lat'] as number | undefined) ?? 0,
      ],
      burnPurpose: feature.properties?.['burn_purpose'],

      pointFeature: pointFeature,
      pointFeatures: [pointFeature],
      polygonFeatures: [feature],
    };

    return baseHazardEntity;
  }

  enrichFeatures(features: Feature<Geometry, GeoJsonProperties>[], hazardModel: GetHazardsModel) {
    return features.map((feature, index) => this.enrichFeature(feature, hazardModel, index));
  }

  enrichFeature(
    feature: Feature<Geometry, GeoJsonProperties>,
    hazardModel: GetHazardsModel,
    index?: number
  ): Feature<Geometry, GeoJsonProperties> {
    const updatedAt = DateTime.fromISO(hazardModel['published-date-time'] ?? hazardModel['updated-date-time'] ?? '');

    return {
      ...feature,
      // TODO: Type the properties object
      properties: {
        ...feature.properties,
        id: hazardModel.id,
        // The `timeAwareFeatureId` is used to uniquely identify the feature on the map.
        // It is a combination of the hazard id and the updated date-time.
        // This is to ensure that the feature is updated on the map when the hazard id remains the same
        // but the hazard has been updated.
        // We add an index, if given, to allow for multiple features within a
        // single event to be differentiated between. Without, this was causing
        // warnings with multiple markers to only display a single marker.
        timeAwareFeatureId: `${hazardModel.id}-${updatedAt?.toMillis()}${index !== undefined && '-' + index.toString()}`,
        featureId: feature.id,
        entityType: hazardModel.entityType,
        entitySubType: hazardModel.entitySubType,
        eventId: hazardModel?.event,
        updatedAt: hazardModel['published-date-time'],
        ...this.composeFireShapeDetails(hazardModel),
      },
    };
  }

  /**
   * Note that this assumes that only one polygon feature is present in the geoSourceFeatures. (Business decision)
   */
  composeFireShapeDetails(hazardModel: GetHazardsModel) {
    const geoSource = hazardModel?.['geo-source'] as FeatureCollection;
    const geoSourceFeatures = geoSource?.['features'] ?? [];

    const polygonFeature = geoSourceFeatures
      .filter((feature) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')
      ?.at(0);

    if (
      !polygonFeature ||
      (hazardModel.entityType as HazardType) !== HazardType.Incident ||
      (hazardModel.entitySubType as IncidentSubType) === IncidentSubType.EarthquakeIncidentEntity ||
      (!polygonFeature.properties?.['date-time'] && !hazardModel['published-date-time'])
    )
      return {};

    const fireShapeUpdatedAt = DateTime.fromISO(polygonFeature.properties?.['date-time'] ?? hazardModel['published-date-time']).toFormat(
      'dd/MM/yyyy HH:mm a'
    );

    return {
      fireShapeLabel: `Incident Area as at: ${fireShapeUpdatedAt}`,
    };
  }

  composeIncidentReportedNear(incidentModel: GetIncidentsModel): string {
    const streetName = incidentModel['location']?.value;
    const nearestCrossStreet = incidentModel['nearest-cross-street']?.value;
    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase();
    const isEarthquake = incidentModel.entitySubType === EntitySubType.EarthquakeIncidentEntity;

    let content = '';

    if (streetName) {
      content = `${streetName.toUpperCase()}`;
    }

    if (isEarthquake) return content;

    if (nearestCrossStreet) {
      content += streetName?.length ? ' and ' : '';
      content += `${nearestCrossStreet.toUpperCase()}`;
    }

    if (suburb && content?.length) {
      content += `, in ${suburb}`;
    }

    return content;
  }

  composeIncidentDescription(incidentModel: GetIncidentsModel): string {
    const locationValue = incidentModel.location?.value ?? '';
    if (Boolean(locationValue) && incidentModel.entitySubType === EntitySubType.EarthquakeIncidentEntity) {
      return locationValue.toUpperCase();
    }

    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase() ?? '';
    const lgas = incidentModel['lga']?.map((lga) => lgasNameMapping.get(lga)?.toUpperCase() ?? lga.toUpperCase()).join(', ') ?? '';

    let content = '';

    if (suburb) {
      content = `${suburb}`;
    }

    if (suburb && lgas) {
      content += ', ';
    }

    if (lgas) {
      content += `${lgas}`;
    }

    return content;
  }

  /**
   * Compose warning description
   * @param warningModel - The warning model
   */
  composeWarningDescription(warningModel: GetWarningsModel): string {
    const actionStatement = warningModel['action-statement']?.toUpperCase();
    const headline = warningModel['headline'];

    if (actionStatement?.length) {
      return `${actionStatement} - ${headline}`;
    }

    return headline ?? '';
  }
}
